import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { BizouLogo } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";

const REFRESH_INTERVAL = 30000;

const CHECK_LABELS = {
  mongodb: { fr: "Base de données", en: "Database" },
  object_storage: { fr: "Stockage des fichiers", en: "File storage" },
  stripe: { fr: "Paiements Stripe", en: "Stripe payments" },
  brevo: { fr: "Service d'email", en: "Email service" },
  emergent_auth: { fr: "Authentification", en: "Authentication" },
  env_vars: { fr: "Configuration", en: "Configuration" },
  scheduler_jobs: { fr: "Tâches planifiées", en: "Scheduled jobs" },
};

export default function StatusPage() {
  const { t, lang } = useTranslation();
  const dateLocale = lang === "fr" ? frLocale : undefined;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/health/deep`, { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
      setLastChecked(new Date());
    } catch (e) {
      setError(e.message || "fetch_failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const overall = data?.status || (error ? "down" : "loading");

  const getOverallColor = () => {
    if (overall === "healthy") return { bg: "bg-[#E8FFF5]", text: "text-[#0F7A4F]", dot: "bg-[#0F7A4F]" };
    if (overall === "degraded") return { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" };
    if (overall === "down") return { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" };
    return { bg: "bg-stone-100", text: "text-[#78716C]", dot: "bg-stone-400" };
  };
  const overallColor = getOverallColor();

  const getOverallLabel = () => {
    if (overall === "healthy") return t("status.allOperational");
    if (overall === "degraded") return t("status.partialOutage");
    if (overall === "down") return t("status.majorOutage");
    return t("status.checking");
  };

  const renderCheckRow = (key, value) => {
    const label = CHECK_LABELS[key]?.[lang] || key;
    let ok = false;
    let detail = "";
    if (key === "scheduler_jobs") {
      ok = typeof value === "number" && value > 0;
      detail = `${value} ${lang === "fr" ? "actives" : "active"}`;
    } else {
      ok = value === "ok";
      detail = ok ? t("status.operational") : (typeof value === "string" ? value : t("status.degraded"));
    }
    return (
      <div key={key} data-testid={`status-check-${key}`} className="flex items-center justify-between py-4 px-5 border-b border-[#F0E8EB] last:border-0">
        <div className="flex items-center gap-3 min-w-0">
          {ok ? (
            <CheckCircle2 className="w-5 h-5 text-[#0F7A4F] shrink-0" strokeWidth={2} />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" strokeWidth={2} />
          )}
          <span className="text-[#1C1917] font-medium text-sm truncate">{label}</span>
        </div>
        <span className={`text-xs font-medium ${ok ? "text-[#0F7A4F]" : "text-amber-700"} whitespace-nowrap`}>
          {detail}
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDF8F4]" data-testid="status-page">
      <header className="bg-white border-b border-[#F0E8EB]">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917]" data-testid="status-back-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden md:inline text-sm">{t("common.back")}</span>
          </a>
          <a href="/"><BizouLogo size="md" /></a>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 md:py-16">
        <h1 className="text-3xl md:text-4xl font-medium text-[#1C1917] mb-3 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t("status.title")}
        </h1>
        <p className="text-[#78716C] text-center mb-10 max-w-xl mx-auto">
          {t("status.subtitle")}
        </p>

        {/* Overall banner */}
        <div className={`${overallColor.bg} rounded-2xl p-6 mb-6 flex items-center justify-between`} data-testid="status-overall-banner">
          <div className="flex items-center gap-4">
            <span className="relative flex h-3 w-3">
              {overall === "healthy" && <span className={`absolute inline-flex h-full w-full rounded-full ${overallColor.dot} opacity-60 animate-ping`} />}
              <span className={`relative inline-flex h-3 w-3 rounded-full ${overallColor.dot}`} />
            </span>
            <div>
              <p className={`font-semibold text-lg ${overallColor.text}`} data-testid="status-overall-label">
                {getOverallLabel()}
              </p>
              {lastChecked && (
                <p className="text-xs text-[#78716C] mt-0.5">
                  {t("status.lastChecked")} {format(lastChecked, lang === "fr" ? "d MMMM yyyy 'à' HH:mm:ss" : "MMMM d, yyyy 'at' HH:mm:ss", { locale: dateLocale })}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            disabled={loading}
            className="rounded-full border-stone-300 text-[#1C1917] hover:bg-white"
            data-testid="status-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden md:inline ml-2 text-xs">{t("status.refresh")}</span>
          </Button>
        </div>

        {/* Checks list */}
        <div className="bg-white rounded-2xl border border-[#F0E8EB] overflow-hidden mb-6" data-testid="status-checks-list">
          {error && !data && (
            <div className="p-6 text-center" data-testid="status-error">
              <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-[#1C1917] font-medium">{t("status.unreachable")}</p>
              <p className="text-xs text-[#78716C] mt-1">{error}</p>
            </div>
          )}
          {data?.checks && Object.entries(data.checks).map(([k, v]) => renderCheckRow(k, v))}
        </div>

        {/* Footer note */}
        <p className="text-xs text-[#78716C] text-center max-w-xl mx-auto leading-relaxed">
          {t("status.promiseNote")}
        </p>
      </main>
    </div>
  );
}
